// utils
import { fetcher, poster, endpoints } from '../utils/axios';

/**
 * Creates a new job.
 * @param {Object} payload - The object containing the job information to add.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function createJob(payload) {
    const URL = endpoints.job.createJob;
    return await poster([URL, payload])
}

/**
 * Updates an existing job.
 * @param {Object} payload - The object containing the job information to update.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function jobUpdated(payload) {
    const URL = endpoints.job.jobUpdated;
    return await poster([URL, payload])
}

/**
 * Creates a duplicate of an existing job.
 * @param {Object} payload - The object containing the job id to duplicate.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function createDuplicateJob(payload) {
    const URL = endpoints.job.createDuplicateJob;
    return await poster([URL, payload])
}

/**
 * Retrieves the list of jobs for a company.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @param {string} payload.id - The id of the company to fetch the jobs for.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getJobList(payload) {
    const URL = endpoints.job.getJobList;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the data for the interpreter job dashboard.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getDashboardData(payload) {
    const URL = endpoints.job.getDashboardData;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the list of all jobs available in the system.
 * @param {Object} payload - The object containing the query parameters for the request.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getAllJobList(payload) {
    const URL = endpoints.job.getAllJobList;
    return await fetcher([URL, { params: payload }])
}

/**
 * Deletes a job from the database.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the job to delete.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function deleteJob(payload) {
    const URL = endpoints.job.deleteJob;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the details of a job.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the job to fetch.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */

export async function jobDetails(payload) {
    const URL = endpoints.job.jobDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Retrieves the company details for a job.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'id' of the job to fetch the company details for.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */
export async function getCompanyDetails(payload) {
    const URL = endpoints.job.getCompanyDetails;
    return await fetcher([URL, { params: payload }])
}

/**
 * Hides a job from the job listing.
 * @param {Object} payload - The object containing the job information to hide.
 * @param {string} payload.jobId - The id of the job to hide.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */

export async function setHideJob(payload) {
    const URL = endpoints.job.setHideJob;
    return await poster([URL, payload]);
}

/**
 * Retrieves the list of jobs for use outside of the platform.
 * @param {Object} payload - The object containing the query parameters for the request.
 *                          The required parameter is 'search' query string to search for jobs.
 * @returns {Promise} - A promise that resolves or rejects based on the response from the server.
 */

export async function getJobsForExternalUse(payload) {
    const URL = endpoints.job.getJobsForExternalUse;
    return await fetcher([URL, { params: payload }])
}
